'use client'

import { useEffect, useMemo, useState } from 'react'
import { IconCircleX } from '@tabler/icons-react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { useDisplayCart } from 'modules/cart'
import {
  extractCustomerFromClientSession,
  extractTokenFromClientSession} from 'modules/customer'
import { getQueryClient, QueryKey } from 'shared/api'
import Button from 'shared/ui/Button'
import { ButtonSizes } from 'shared/ui/Button/Button.enums'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { gtmCancelSaleOrderEvent } from '../analytics'
import { cancelSaleOrder } from '../api'
import { SaleOrder } from '../types'
import {
  isAvailableToCancelOrder,
  isCancelingBlockedByEditMode
} from '../utils/saleOrders'

import styles from './CancelSaleOrderModal.module.scss'

const CancelSaleOrderModal = ({
  order,
  testId,
  triggerButtonSize = ButtonSizes.medium
}: {
  order: SaleOrder
  testId?: string
  triggerButtonSize?: ButtonSizes
}) => {
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const user = extractCustomerFromClientSession(session)
  const [open, setOpen] = useState(false)
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
  const router = useRouter()
  const pathname = usePathname()
  const cart = useDisplayCart()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const isBlockedByEditMode = useMemo(
    () => isCancelingBlockedByEditMode(order, cart),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, cart?.saleOrderId, cart?.editMode]
  )

  useEffect(() => {
    const checkTime = () => {
      setIsAvailable(isAvailableToCancelOrder(order.deliverySlot?.date))
    }

    if (open) checkTime()
  }, [open, order.deliverySlot?.date])

  const handleCancelOrder = () => {
    cancelSaleOrder({ id: order.id, token })
      .then(async () => {
        if (user) {
          gtmCancelSaleOrderEvent(order, user)
        }
        handleClose()
        await getQueryClient().invalidateQueries({
          queryKey: [QueryKey.profileOrders]
        })
        void router.replace(pathname)
      })
      .catch(err => {
        toast.error(getApiErrors(err))
      })
  }

  return (
    <div id={testId}>
      <Button
        id='cancel_order_button'
        variant='neutralOutline'
        startIcon={<IconCircleX />}
        onClick={handleOpen}
        size={triggerButtonSize}
        disabled={
          ['delivered', 'cancelled', 'in_progress'].includes(order.status) ||
          isAvailable === false
        }
        aria-label='Anuluj zamówienie'
        title={
          isBlockedByEditMode
            ? 'Anulowanie zamówienia jest niedostępne podczas edycji zamówienia'
            : undefined
        }
        loading={!cart}
      >
        {!cart ? '' : 'Anuluj'}
      </Button>
      <Modal id='cancel-order-modal' open={open} onClose={handleClose}>
        <ModalTitle>Anulowanie zamówienia</ModalTitle>
        <ModalDescription>
          Zamówienie może być anulowane do godziny 23:50 do niedzieli w danym
          cyklu zamówień.*
          <small className={styles.small}>
            *W Lokalnym Rolniku obowiązują tygodniowe cykle zakupowe. Zamówienia
            zbieramy od poniedziałku do niedzieli, do godz. 23:50.
          </small>
        </ModalDescription>
        <div className={styles.buttonsWrapper}>
          <Button
            id='cancel_order_confirmation'
            fullWidth
            type='button'
            disabled={!isAvailable}
            onClick={handleCancelOrder}
          >
            {isAvailable ? 'Potwierdź' : 'Niedostępne'}
          </Button>
          <Button
            id='cancel_order_close_modal'
            fullWidth
            variant='neutralOutline'
            onClick={handleClose}
          >
            Zamknij
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default CancelSaleOrderModal
